import { AddCircleOutlined, BoltOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { NEUTRAL, RADIUS } from "../../../../../theme/tokens";
import type { AutoApproveRule } from "../../../../../utils/models";
import { AutoApproveRuleRow } from "./AutoApproveRuleRow";
import type { JobCatalogState } from "./autoApproveConsts";

const L = "department.config.staffApp.autoApprove.list";

type Props = {
	rules: AutoApproveRule[];
	jobCatalog: JobCatalogState;
	/** Adds an empty rule and returns its id, so the new line opens for editing. */
	onAddRule: () => string;
	onChange: (index: number, patch: Partial<AutoApproveRule>) => void;
	onDelete: (index: number) => void;
};

/** The department's rules in the order they're checked, each a line that opens to edit. */
export const AutoApproveRulesList: FC<Props> = ({ rules, jobCatalog, onAddRule, onChange, onDelete }) => {
	const { t } = useTranslation();
	const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
	// DEFAULT: a rule loaded without an id (never saved) is keyed by its place in the list.
	const keyOf = (rule: AutoApproveRule, index: number): string => rule.id ?? `index-${index}`;
	const toggle = (key: string) =>
		setOpenIds((current) => {
			const next = new Set(current);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	const add = () => {
		const id = onAddRule();
		setOpenIds((current) => new Set(current).add(id));
	};
	const addButton = (testId: string, variant: "secondary" | "primary") => (
		<SystemButton
			buttonVariant={variant}
			size="small"
			noFullWidthMobile
			leadingIcon={<AddCircleOutlined fontSize="small" />}
			onClick={add}
			data-testid={testId}
		>
			{t(`${L}.add`)}
		</SystemButton>
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
			<SettingsSection
				headerOnly
				title={t(`${L}.title`)}
				description={t(`${L}.description`)}
				dataTestId="dept-config-auto-approve-rules-header"
				action={addButton("dept-config-auto-approve-add-row", "secondary")}
			/>
			{rules.length === 0 ? (
				<EmptyState
					icon={<BoltOutlined />}
					title={t("department.config.staffApp.autoApprove.empty.title")}
					hint={t("department.config.staffApp.autoApprove.empty.hint")}
					action={addButton("dept-config-auto-approve-empty-add", "secondary")}
					dataTestId="dept-config-auto-approve-empty"
				/>
			) : (
				<Box sx={{ border: `1px solid ${NEUTRAL[200]}`, borderRadius: `${RADIUS.CONTROL}px`, overflow: "hidden", backgroundColor: "background.paper" }}>
					{rules.map((rule, index) => {
						const key = keyOf(rule, index);
						return (
							<AutoApproveRuleRow
								key={key}
								rule={rule}
								index={index}
								jobCatalog={jobCatalog}
								open={openIds.has(key)}
								onToggle={() => toggle(key)}
								onChange={(patch) => onChange(index, patch)}
								onDelete={() => onDelete(index)}
							/>
						);
					})}
				</Box>
			)}
		</Box>
	);
};
